import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from "lucide-react";
import Header from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  question: "",
  answer: "",
  category: "General",
  order: 999,
  active: true,
};

/**
 * /admin/faqs — Owner-only FAQ editor.
 * CRUD over the FAQ catalog with category grouping, ordering, active-toggle,
 * and a soft-delete (delete is hard but rare). Mirrors AdminPartners layout.
 */
export default function AdminFaqs() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/faqs`);
      setFaqs(data?.faqs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load FAQs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="pt-32 text-center text-neutral-400">Owner only.</div>
      </div>
    );
  }

  const upd = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function save() {
    if (saving) return;
    if (!form?.question?.trim() || !form?.answer?.trim()) {
      toast.error("Question and answer required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: parseInt(form.order, 10) || 999,
      };
      if (form.faq_id) {
        await axios.put(`${API}/admin/faqs/${form.faq_id}`, payload);
        toast.success("FAQ updated.");
      } else {
        await axios.post(`${API}/admin/faqs`, payload);
        toast.success("FAQ created.");
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(f) {
    try {
      await axios.put(`${API}/admin/faqs/${f.faq_id}`, { active: !f.active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete this FAQ? "${f.question.slice(0, 60)}"`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${f.faq_id}`);
      toast.success("FAQ deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  // Group by category, preserving order
  const grouped = faqs.reduce((acc, f) => {
    const c = f.category || "General";
    if (!acc[c]) acc[c] = [];
    acc[c].push(f);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-faqs-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
              Admin
            </div>
            <h1 className="font-display text-3xl text-white leading-none mt-1">
              FAQ Editor
            </h1>
            <p className="text-xs text-neutral-500 mt-1">
              {faqs.length} FAQs · {faqs.filter((f) => f.active).length} active
            </p>
          </div>
          <button
            onClick={() => setForm({ ...EMPTY })}
            data-testid="admin-faqs-add-btn"
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> Add FAQ
          </button>
        </div>

        {/* Form */}
        {form && (
          <div className="mb-8 bg-[#0F0F0F] border-2 border-[#D4AF37] p-5" data-testid="admin-faq-form">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl">
                {form.faq_id ? "Edit FAQ" : "New FAQ"}
              </div>
              <button onClick={() => setForm(null)} className="text-neutral-500 hover:text-white">
                <X size={16} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Question
                </label>
                <input
                  data-testid="admin-faq-q"
                  className="input-omni w-full" value={form.question} onChange={upd("question")}
                  placeholder="How do streaks work?"
                  maxLength={300}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Answer
                </label>
                <textarea
                  data-testid="admin-faq-a"
                  className="input-omni w-full h-32 resize-y" value={form.answer} onChange={upd("answer")}
                  placeholder="Explain in plain English…"
                  maxLength={4000}
                />
                <div className="text-[10px] text-neutral-600 text-right mt-1">{form.answer.length}/4000</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Category
                </label>
                <input
                  data-testid="admin-faq-cat"
                  className="input-omni w-full" value={form.category} onChange={upd("category")}
                  placeholder="Eggs & Tiers"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Order (lower = first)
                </label>
                <input
                  data-testid="admin-faq-order"
                  type="number"
                  className="input-omni w-full" value={form.order} onChange={upd("order")}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={upd("active")} data-testid="admin-faq-active" />
                Active (visible on /faq)
              </label>
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-faq-save"
              className="mt-4 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={14} /> {saving ? "Saving…" : form.faq_id ? "Save Changes" : "Create FAQ"}
            </button>
          </div>
        )}

        {/* Grouped list */}
        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : faqs.length === 0 ? (
          <div className="text-center text-neutral-500 py-12 border border-zinc-900 bg-[#0F0F0F]">
            No FAQs yet. Click <span className="text-[#D4AF37]">Add FAQ</span> to seed the first one.
          </div>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <section key={cat} className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
                {cat} · {items.length}
              </div>
              <div className="space-y-2">
                {items.map((f) => (
                  <div
                    key={f.faq_id}
                    data-testid={`admin-faq-row-${f.faq_id}`}
                    className={
                      "bg-[#0F0F0F] border-2 p-3 flex items-start gap-3 flex-wrap " +
                      (f.active ? "border-zinc-800" : "border-zinc-900 opacity-60")
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold leading-snug">{f.question}</div>
                      <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{f.answer}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-2">
                        order {f.order} · {f.active ? "active" : "hidden"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => toggleActive(f)}
                        className="p-2 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                        title={f.active ? "Hide" : "Show"}
                      >
                        {f.active ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        onClick={() => setForm({ ...f })}
                        className="p-2 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => remove(f)}
                        className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}
